/**
 * The settings screen ([D-132](../../../docs/DECISIONS.md#d-132),
 * [D-133](../../../docs/DECISIONS.md#d-133), mockup 19).
 *
 * Everything here is a child's own to choose, and nothing a grown-up needs to
 * guard — those live behind the grown-ups' password. Grouped the way the
 * mockup groups them, because a list of eight switches with no headings is a
 * list a nine-year-old stops reading after three.
 *
 * Every switch is a real `role="switch"` that says *On* or *Off* to a screen
 * reader; the green is for everyone else.
 */

import '@capytalk/tokens/blocks/settings.css';
import { type ReactNode, useEffect, useState } from 'react';
import Dashboard from './Dashboard.js';
import Flag from './Flag.js';
import type { Client } from './api.js';
import HolidayScreen from './Holiday.js';
import Placement from './Placement.js';
import Sneakers from './Sneakers.js';
import { type Learning, setLearning } from './language.js';
import { type Prefs, loadPrefs, savePrefs } from './prefs.js';

function Row({
  icon,
  label,
  hint,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly hint?: string;
}) {
  return (
    <>
      <span className="ic" aria-hidden="true">
        {icon}
      </span>
      <span className="tx">
        {label}
        {hint !== undefined && (
          <span className="tiny" style={{ display: 'block' }}>
            {hint}
          </span>
        )}
      </span>
    </>
  );
}

function Switch({
  icon,
  label,
  hint,
  on,
  last = false,
  onChange,
}: {
  readonly icon: string;
  readonly label: string;
  readonly hint?: string;
  readonly on: boolean;
  readonly last?: boolean;
  readonly onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className="lrow"
      style={{
        width: '100%',
        background: 'none',
        border: 0,
        borderBottom: last ? 0 : undefined,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        minHeight: 56,
      }}
      onClick={() => {
        onChange(!on);
      }}
    >
      <Row icon={icon} label={label} {...(hint === undefined ? {} : { hint })} />
      <span className={on ? 'sw' : 'sw off'} aria-hidden="true" />
    </button>
  );
}

function Link({
  icon,
  label,
  hint,
  last = false,
  onClick,
}: {
  readonly icon: string;
  readonly label: string;
  readonly hint: string;
  readonly last?: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="lrow"
      style={{
        width: '100%',
        background: 'none',
        border: 0,
        borderBottom: last ? 0 : undefined,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        minHeight: 56,
      }}
      onClick={onClick}
    >
      <Row icon={icon} label={label} hint={hint} />
      <span aria-hidden="true" style={{ color: '#8C8373', fontWeight: 800 }}>
        ›
      </span>
    </button>
  );
}

/** One language in the picker, as `course.list` describes it. */
type CourseChoice = Awaited<
  ReturnType<Client['course']['list']['query']>
>['courses'][number];

/**
 * *The language I'm learning* ([D-189](../../../docs/DECISIONS.md#d-189)).
 *
 * One row per language the server has loaded, as radio buttons: exactly one is
 * always chosen. Switching keeps everything — the stars in each language stay
 * where they were left, and the acorns, the wardrobe and the streak are the
 * child's, not the language's. With one language loaded the row is still
 * there, so a child can see what they are learning and that more is coming.
 *
 * Nothing is drawn until the list arrives, and nothing at all if it will not:
 * a settings screen missing a card is better than one with a broken card.
 */
export function Languages({
  client,
  onChosen,
}: {
  readonly client: Client;
  readonly onChosen?: (next: Learning) => void;
}) {
  const [courses, setCourses] = useState<readonly CourseChoice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const answer = await client.course.list.query();
        if (live) setCourses(answer.courses);
      } catch {
        if (live) setCourses([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [client]);

  if (courses === null || courses.length === 0) return null;

  const choose = async (course: CourseChoice) => {
    if (course.current || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const answer = await client.course.choose.mutate({ courseId: course.courseId });
      setCourses(answer.courses);
      const next = { courseId: course.courseId, l2: course.l2, title: course.title };
      setLearning(next);
      onChosen?.(next);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="kicker" style={{ margin: '14px 0 4px' }}>
        LANGUAGE
      </div>
      <div className="card" role="radiogroup" aria-label="The language I'm learning">
        {courses.map((course, at) => (
          <button
            key={course.courseId}
            type="button"
            role="radio"
            aria-checked={course.current}
            className="lrow"
            disabled={busy}
            style={{
              width: '100%',
              background: 'none',
              border: 0,
              borderBottom: at === courses.length - 1 ? 0 : undefined,
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
              minHeight: 56,
            }}
            onClick={() => {
              void choose(course);
            }}
          >
            <Row
              icon={<Flag l2={course.l2} />}
              label={course.title}
              {...(course.current ? { hint: 'Learning now' } : {})}
            />
            <span
              aria-hidden="true"
              style={{
                fontWeight: 800,
                // The readable green, for the one chosen (D-070).
                color: course.current ? 'var(--green-ink)' : 'transparent',
              }}
            >
              ✓
            </span>
          </button>
        ))}
      </div>
      <p className="tiny" style={{ margin: '6px 4px 0' }}>
        {courses.length === 1
          ? 'More languages are on the way.'
          : 'Your stars in each language stay where you left them.'}
      </p>
      {failed && (
        <p className="tiny" role="alert" style={{ margin: '6px 4px 0' }}>
          That did not work. Please try again.
        </p>
      )}
    </>
  );
}

export default function Settings({
  client,
  learnerId,
  sound,
  onSound,
  onLeave,
  onLanguage,
}: {
  readonly client: Client;
  readonly learnerId: string;
  readonly sound: boolean;
  readonly onSound: (on: boolean) => void;
  readonly onLeave: () => void;
  /** Told when the child switches language, so the app can start afresh. */
  readonly onLanguage?: (next: Learning) => void;
}) {
  const [prefs, setPrefs] = useState(() => loadPrefs(learnerId));
  const [open, setOpen] = useState<'settings' | 'placing' | 'holiday' | 'seen'>(
    'settings',
  );

  if (open === 'placing') return <Placement client={client} onDone={onLeave} />;
  if (open === 'seen') {
    // The grown-ups' page, about this child alone: anything a grown-up can see
    // about a child, the child can see too (D-039, D-194).
    return (
      <Dashboard
        title="What grown-ups can see"
        intro="This is about you. Your grown-ups see the same page."
        load={(days) => client.dashboard.mine.query({ days })}
        onClose={() => {
          setOpen('settings');
        }}
      />
    );
  }
  if (open === 'holiday') {
    return (
      <HolidayScreen
        client={client}
        onLeave={() => {
          setOpen('settings');
        }}
      />
    );
  }

  const set = (change: Partial<Prefs>) => () => {
    const next = { ...prefs, ...change };
    savePrefs(learnerId, next);
    setPrefs(next);
  };
  const toggle = (name: keyof Prefs) => (on: boolean) => {
    set({ [name]: on })();
  };

  return (
    <div className="pad grow">
      <h2>Settings</h2>
      <Sneakers
        text="You choose how the app works."
        audioRef={null}
        size={56}
        speak={false}
      />

      <Languages
        client={client}
        {...(onLanguage === undefined ? {} : { onChosen: onLanguage })}
      />

      <div className="kicker" style={{ margin: '14px 0 4px' }}>
        SOUND
      </div>
      <div className="card">
        <Switch
          icon="🗣️"
          label="Sneakers talks"
          hint="He stays on screen either way. Tap him to hear a line."
          on={prefs.sneakersTalks}
          onChange={toggle('sneakersTalks')}
        />
        <Switch icon="🔊" label="Sound effects" on={sound} onChange={onSound} />
        <Switch
          icon="🎤"
          label="Use the microphone"
          hint="Off: you just say it out loud yourself"
          on={prefs.useMic}
          last
          onChange={toggle('useMic')}
        />
      </div>

      <div className="kicker" style={{ margin: '14px 0 4px' }}>
        READING AND SEEING
      </div>
      <div className="card">
        <Switch
          icon="🔤"
          label="Easier reading"
          hint="More space between letters, words and lines, and a cream page."
          on={prefs.easierReading}
          onChange={toggle('easierReading')}
        />
        <Switch
          icon="🔍"
          label="Bigger text"
          on={prefs.biggerText}
          onChange={toggle('biggerText')}
        />
        <Switch
          icon="🎐"
          label="Less movement"
          hint="Sneakers moves more gently"
          on={prefs.lessMovement}
          last
          onChange={toggle('lessMovement')}
        />
      </div>
      {/* Right under the reading switches, so the difference is seen, not described. */}
      <p className="bubble" style={{ marginTop: 10 }}>
        The cat is on the red mat.
      </p>

      <div className="kicker" style={{ margin: '14px 0 4px' }}>
        STREAK
      </div>
      <div className="card">
        <Link
          icon="🏖️"
          label="Going away"
          hint="Keep your streak safe — free"
          last
          onClick={() => {
            setOpen('holiday');
          }}
        />
      </div>

      <div className="kicker" style={{ margin: '14px 0 4px' }}>
        LEARNING
      </div>
      <div className="card">
        <Link
          icon="🧭"
          label="Find my starting place again"
          hint="A few quick questions. Nothing you have done is lost."
          onClick={() => {
            setOpen('placing');
          }}
        />
        <Link
          icon="👀"
          label="What grown-ups can see"
          hint="The same page your grown-ups see about you"
          last
          onClick={() => {
            setOpen('seen');
          }}
        />
      </div>

      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 20 }}
        onClick={onLeave}
      >
        Back
      </button>
    </div>
  );
}
